import { addDevVars } from "@/dev/initDev.ts";
import { useSyncExternalStore } from "react";

//#region CacheValue type implementation ("methods")
type CacheValue = [number, string];

function makeValue(refcount: number, url: string): CacheValue {
    return [refcount, url];
}

function getUrl(value: CacheValue) {
    return value[1];
}

function getRefcount(value: CacheValue) {
    return value[0];
}

function increaseRefcount(value: CacheValue) {
    value[0]++;
}

function decreaseRefcount(value: CacheValue) {
    value[0]--;
}

//#endregion

const gcPeriod = 1000 * 60;

interface IBlobCache {
    destroy(): void;
    get(data: string, mimeType?: string): string;
    subscribe(data: string, mimeType?: string): () => void;
}

export class BlobCache implements IBlobCache {
    private readonly urlCache = new Map<string, CacheValue>();
    private readonly gcInterval = setInterval(() => this.garbageCollect(), gcPeriod);

    destroy() {
        clearInterval(this.gcInterval);
        for (const entry of this.urlCache.values()) {
            URL.revokeObjectURL(getUrl(entry));
        }
    }

    private getEntry(data: string, mimeType?: string) {
        const key = data;
        let entry = this.urlCache.get(key);
        if (!entry) {
            _debugLog("create", data);
            entry = makeValue(0, createBlob(data, mimeType));
            this.urlCache.set(key, entry);
        }
        return entry;
    }
    get(data: string, mimeType?: string) {
        return getUrl(this.getEntry(data, mimeType));
    }

    subscribe(data: string, mimeType?: string) {
        const entry = this.getEntry(data, mimeType);
        _debugLog("subscribe", data);
        increaseRefcount(entry);
        return () => {
            _debugLog("unsubscribe", data);
            decreaseRefcount(entry);
        };
    }

    /** NB: This should always be invoked outside the React render loop so it does not remove entries that have been gotten but not subscribed */
    garbageCollect() {
        for (const [key, entry] of this.urlCache.entries()) {
            if (getRefcount(entry) === 0) {
                _debugLog("gc", key);
                this.urlCache.delete(key);
                URL.revokeObjectURL(getUrl(entry));
            }
        }
    }
}

/** Creates a regular data URL. */
export class FakeBlobCache implements IBlobCache {
    get(data: string, mimeType?: string) {
        const firstBytes = atob(data.slice(0, 20));
        mimeType ??= detectMime(firstBytes);
        return `data:${mimeType};base64,${data}`;
    }
    subscribe(_data: string, _mimeType?: string) {
        return () => {};
    }
    destroy() {}
}

export const blobCache: IBlobCache = /*#__PURE__*/ new BlobCache();

addDevVars({ blobCache });

/** Takes a base64-encoded string as input and returns a corresponding blob: URL */
export function useBlobCache(data: string, mimeType?: string) {
    return useSyncExternalStore(
        () => blobCache.subscribe(data),
        () => blobCache.get(data, mimeType),
    );
}

function detectMime(binary: string) {
    return binary.startsWith("<") ? "image/svg+xml" : binary.startsWith("PNG") ? "image/png" : "image/jpeg";
}

function createBlob(data: string, mimeType?: string) {
    const binary = atob(data);
    const mime = mimeType ?? detectMime(binary);
    const blob = new Blob([binary], { type: mime });
    _debugLog("createBlob", data, mime);
    return URL.createObjectURL(blob);
}

// function _debugLog(action: string, data: string, ...args: any[]) {
//     const l = data.length / 2;
//     console.debug("BlobCache.%s (%s)", action, data.slice(l, l + 10), ...args);
// }

function _debugLog(..._args: any[]) {}
